import { useCallback } from 'react'

import { ProjectType } from './constants'
import { ApartmentRouteContextKey, HouseAndLandRouteContextKey } from './types'
import {
  ApartmentRoute,
  ApartmentRouteParams,
  DefaultRouteParamKey,
  HouseAndLandRoute,
  HouseAndLandRouteParams,
  InvalidParsedRoute,
  ParsedApartmentRoute,
  ParsedHouseAndLandRoute,
} from './types/mapActionRoute'

const ALL_ROUTES = {
  ...ApartmentRoute,
  ...HouseAndLandRoute,
  AreaView: ApartmentRoute.AreaView,
  AreaViewHouseAndLand: HouseAndLandRoute.AreaView,
}

const VALID_ROUTE_PARAM_KEYS: string[] = [
  ...Object.values(DefaultRouteParamKey),
  ...Object.values(ApartmentRouteContextKey),
  ...Object.values(HouseAndLandRouteContextKey),
]

interface UseActionRouteUtilsArgs {
  currentProjectType: ProjectType
}

const useMapActionRouteUtils = ({
  currentProjectType,
}: UseActionRouteUtilsArgs) => {
  const validateRouteParams = useCallback<
    (routeParams: ApartmentRouteParams | HouseAndLandRouteParams) => boolean
  >((routeParams) => {
    const hasInvalidParam = Object.keys(routeParams).some(
      (key) => !VALID_ROUTE_PARAM_KEYS.includes(key)
    )

    if (hasInvalidParam) {
      return false
    }
    if (
      routeParams[DefaultRouteParamKey.Project] &&
      !routeParams[DefaultRouteParamKey.ProjectType]
    ) {
      return false
    }
    if (
      !routeParams[DefaultRouteParamKey.Project] &&
      routeParams[DefaultRouteParamKey.ProjectType]
    ) {
      return false
    }

    return true
  }, [])

  const validateApartmentActionRoute = useCallback(
    ({
      route,
      routeParams,
      isSoftRouteValidation,
    }: {
      route: ApartmentRoute
      routeParams: ApartmentRouteParams
      isSoftRouteValidation?: boolean
    }) => {
      const isValidRoute = Object.values(
        isSoftRouteValidation ? ALL_ROUTES : ApartmentRoute
      ).includes(route)

      if (
        !routeParams[DefaultRouteParamKey.Project] &&
        route === ApartmentRoute.InteractiveMap
      ) {
        return false
      }
      if (!isValidRoute) {
        return false
      }
      if (route === ApartmentRoute.Building && !routeParams.level) {
        return false
      }
      return validateRouteParams(routeParams)
    },
    [validateRouteParams]
  )

  const validateHouseAndLandActionRoute = useCallback(
    ({
      route,
      routeParams,
      isSoftRouteValidation,
    }: {
      route: HouseAndLandRoute
      routeParams: HouseAndLandRouteParams
      isSoftRouteValidation?: boolean
    }) => {
      const isValidRoute = Object.values(
        isSoftRouteValidation ? ALL_ROUTES : HouseAndLandRoute
      ).includes(route)

      if (
        !routeParams[DefaultRouteParamKey.Project] &&
        route === HouseAndLandRoute.InteractiveMap
      ) {
        return false
      }
      if (!isValidRoute) {
        return false
      }
      if (route === HouseAndLandRoute.Precinct && !routeParams.precinct) {
        return false
      }
      if (
        route === HouseAndLandRoute.Stage &&
        (!routeParams.precinct || !routeParams.stage)
      ) {
        return false
      }
      return validateRouteParams(routeParams)
    },
    [validateRouteParams]
  )

  const getApartmentRouteParams = useCallback<
    (searchParams: URLSearchParams) => ApartmentRouteParams
  >((searchParams) => {
    const parsedParams: ApartmentRouteParams = Object.fromEntries(searchParams)
    return parsedParams
  }, [])

  const getHouseAndLandRouteParams = useCallback<
    (searchParams: URLSearchParams) => HouseAndLandRouteParams
  >((searchParams) => {
    const parsedParams: HouseAndLandRouteParams =
      Object.fromEntries(searchParams)
    return parsedParams
  }, [])

  const getPathParams = useCallback((searchParams: URLSearchParams) => {
    const searchParamsCopy = new URLSearchParams(searchParams)
    Object.values(DefaultRouteParamKey).forEach((paramKey) => {
      searchParamsCopy.delete(paramKey)
    })
    return searchParamsCopy.toString()
  }, [])

  const getFullPath = useCallback<
    (route: ApartmentRoute | HouseAndLandRoute, pathParams: string) => string
  >(
    (route, pathParams) =>
      pathParams && route ? `${route}?${pathParams}` : String(route),
    []
  )

  const parseApartmentActionRoute = useCallback<
    (actionRoute: string) => ParsedApartmentRoute | InvalidParsedRoute
  >(
    (actionRoute) => {
      const searchParams = new URLSearchParams(actionRoute)
      const route = (searchParams.get(DefaultRouteParamKey.Page) ||
        '') as unknown as ApartmentRoute
      const pathParams = getPathParams(searchParams)
      const fullpath = getFullPath(route, pathParams)
      const routeParams = getApartmentRouteParams(searchParams)
      const hasProjectParam = !!routeParams[DefaultRouteParamKey.Project]

      const isValid = validateApartmentActionRoute({
        route,
        routeParams,
        isSoftRouteValidation: hasProjectParam,
      })

      if (!isValid) {
        return {
          isValid: false,
        }
      }

      const parsedActionRoute: ParsedApartmentRoute = {
        route,
        fullpath,
        pathParams,
        routeParams,
        projectType: ProjectType.Apartment,
        isValid,
      }
      return parsedActionRoute
    },
    [
      getPathParams,
      getFullPath,
      getApartmentRouteParams,
      validateApartmentActionRoute,
    ]
  )

  const parseHouseAndLandActionRoute = useCallback<
    (actionRoute: string) => ParsedHouseAndLandRoute | InvalidParsedRoute
  >(
    (actionRoute) => {
      const searchParams = new URLSearchParams(actionRoute)
      const route = (searchParams.get(DefaultRouteParamKey.Page) ||
        '') as unknown as HouseAndLandRoute
      const pathParams = getPathParams(searchParams)
      const fullpath = getFullPath(route, pathParams)
      const routeParams = getHouseAndLandRouteParams(searchParams)
      const hasProjectParam = !!routeParams[DefaultRouteParamKey.Project]

      const isValid = validateHouseAndLandActionRoute({
        route,
        routeParams,
        isSoftRouteValidation: hasProjectParam,
      })

      if (!isValid) {
        return {
          isValid: false,
        }
      }

      const parsedActionRoute: ParsedHouseAndLandRoute = {
        route,
        fullpath,
        pathParams,
        routeParams,
        projectType: ProjectType.HouseAndLand,
        isValid,
      }
      return parsedActionRoute
    },
    [
      getPathParams,
      getFullPath,
      getHouseAndLandRouteParams,
      validateHouseAndLandActionRoute,
    ]
  )

  const parseActionRoute = useCallback<
    (
      actionRoute: string
    ) =>
      | ReturnType<typeof parseApartmentActionRoute>
      | ReturnType<typeof parseHouseAndLandActionRoute>
  >(
    (actionRoute) => {
      const searchParams = new URLSearchParams(actionRoute)
      const actionRouteProjectType = searchParams.get(
        DefaultRouteParamKey.ProjectType
      ) as ProjectType | null
      const projectType = actionRouteProjectType || currentProjectType

      if (projectType === ProjectType.Apartment) {
        return parseApartmentActionRoute(actionRoute)
      }

      if (projectType === ProjectType.HouseAndLand) {
        return parseHouseAndLandActionRoute(actionRoute)
      }

      return {
        isValid: false,
      }
    },
    [
      currentProjectType,
      parseApartmentActionRoute,
      parseHouseAndLandActionRoute,
    ]
  )

  return {
    parseActionRoute,
  }
}

export default useMapActionRouteUtils
